import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { EventSelection } from '@/shared/ui';
import EditAiStripModal from './editAiStripModal';
import useEventSelectedInfo from './useEventSelectedInfo';
import './styles.scss';

export type EventSelectedInfoProps = {
  keyRecoil: string;
  componentId: string;
  // propTypes declared this as string, but real callers pass IReportInfo.timezoneOffset (number) - pre-existing gap.
  timezoneOffset: any;
  ecgDataMap?: any;
};

/**
 * Detail panel for the ECG event currently selected in a report tab. Renders a skeleton until the formatted event arrives, then the event selector and each display field - jump-to buttons for start and end time, a min, avg and max breakdown for heart rate, and per capture View buttons - plus the edit strip modal for the capture being viewed.
 *
 * @param props - The panel props, including componentId used to key the event selector.
 * @returns The rendered event detail panel with its selector, field list and edit strip modal.
 */
const EventSelectedInfo = ({
  keyRecoil,
  componentId,
  timezoneOffset,
  ecgDataMap,
}: EventSelectedInfoProps) => {
  const {
    state,
    activeButton,
    isLoadingEcgViewer,
    formatSelectedEvent,
    formatSelectedEventInfo,
    toggleEditAiStripModal,
    updateEditData,
    handleRemoveStrip,
    onChangeSelectedEvent,
    onClickJumpTo,
  } = useEventSelectedInfo({ keyRecoil, timezoneOffset, ecgDataMap });

  if (_.isEmpty(formatSelectedEvent)) {
    return (
      <div className="event-selected-info-wrapper-skeleton">
        <Skeleton height="100%" />
      </div>
    );
  }

  return (
    <div id="event-info" className="event-selected-info-wrapper">
      <div className="event-selected-time">{formatSelectedEvent?.startTime}</div>
      <EventSelection
        onClick={onChangeSelectedEvent}
        data={formatSelectedEventInfo}
        selectedEventIndex={formatSelectedEvent.slectedIindex}
        selectedEvent={formatSelectedEvent}
        componentId={`${componentId}-${activeButton}`}
      />
      <div className="event-selected-info-content">
        {_.map(formatSelectedEvent?.displayEvent, (x) => (
          <div className="event-selected-info-item">
            <div className="event-selected-info-item__title">{`${x.title}:`}</div>
            {x.type === 'ARRAY' ? (
              <div className="event-selected-info-item__list">
                {_.map(x.value, (y, index) => (
                  <div key={`${y.display}-${index}`} className="event-selected-info-item__item">
                    <div className="event-selected-info-item__value">{y.display}</div>
                    <button
                      id={`beats-view-event-btn-${index}`}
                      disabled={state.isLoadingSaveAiReport || isLoadingEcgViewer}
                      onClick={() => {
                        toggleEditAiStripModal(formatSelectedEvent?.event, y);
                      }}
                      className="event-selected-info-item__item-button"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            ) : x.title === 'Start time' || x.title === 'End time' ? (
              <button
                id={x.title === 'Start time' ? 'jump-to-start-time-btn' : 'jump-to-end-time-btn'}
                className="jumpto_btn"
                onClick={() => {
                  onClickJumpTo(formatSelectedEvent, x.title === 'End time' ? 'stop' : 'start');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onClickJumpTo(formatSelectedEvent, x.title === 'End time' ? 'stop' : 'start');
                  }
                }}
              >
                <span className="event-selected-info-item__value">{x.value}</span>
              </button>
            ) : x.title === 'Heart rate' ? (
              <div className="event-selected-info-item__value heart-rate">
                {['Min', 'Avg', 'Max']
                  .filter((label) => label.toLowerCase() in (x.value || {}))
                  .map((label) => (
                    <span className="heart_rate_item" key={label}>
                      <span className="title"> {label}:</span>
                      <span className="value">{x.value[label.toLowerCase()]}</span>
                    </span>
                  ))}
              </div>
            ) : (
              <div className="event-selected-info-item__value">{x.value}</div>
            )}
          </div>
        ))}
      </div>
      <EditAiStripModal
        visible={state.isVisibleEditAiStripModal}
        handleClickCloseButton={() => toggleEditAiStripModal()}
        data={state.selectedData}
        originalData={state.originalSelectedData}
        updateEditData={updateEditData}
        isEditBasicInfo
        handleRemoveStrip={handleRemoveStrip}
        couldRemoveStrip={state.couldRemoveStrip}
      />
    </div>
  );
};

export default EventSelectedInfo;
